import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, Plane, BedDouble, QrCode, Bus, FileWarning, ArrowRight, Crown, CalendarDays,
} from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from 'recharts';
import useFetch from '../../hooks/useFetch';
import useChartMotion from '../../hooks/useChartMotion';
import { useEvents } from '../../context/EventContext';
import { useAuth } from '../../context/AuthContext';
import EventPicker from '../../components/EventPicker';
import {
  Card,
  Stat,
  Loader,
  Button,
  EmptyState,
  SectionTitle,
  stagger,
} from '../../components/UI';
import { fmtNumber, fmtDate, pct } from '../../utils/format';

export default function CorporateDashboard() {
  const { user } = useAuth();
  const { eventId, event, events, loading: eventsLoading } = useEvents();

  const { data: dash, loading } = useFetch(
    eventId ? `/events/${eventId}/dashboard` : null, {}, { skip: !eventId },
  );
  const { data: readiness } = useFetch('/flights/readiness', { event: eventId }, { skip: !eventId });
  const chartMotion = useChartMotion();

  if (eventsLoading) return <Loader full />;

  if (!events.length) {
    return (
      <EmptyState
        message="No events assigned yet"
        hint="Your Golden Lotus coordinator will add your company to an event. It will appear here as soon as it does."
        icon={CalendarDays}
      />
    );
  }

  const compliance = readiness?.summary;
  const chartData = compliance ? [
    { name: 'Docs verified', value: compliance.docsVerified, fill: '#4ADE80' },
    { name: 'Docs pending', value: compliance.docsPending, fill: '#FBBF24' },
    { name: 'Tickets issued', value: compliance.ticketsIssued, fill: '#EFD48A' },
    { name: 'Fully ready', value: compliance.fullyReady, fill: '#60A5FA' },
  ] : [];

  return (
    <motion.div variants={stagger} initial="hidden" animate="show">
      <Card padding={24} style={{ marginBottom: 20, overflow: 'hidden' }}>
        <motion.div
          animate={{ opacity: [0.35, 0.65, 0.35] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute', top: -80, right: -40, width: 280, height: 280,
            background: 'radial-gradient(circle, rgba(239,212,138,0.11), transparent 70%)',
            filter: 'blur(38px)', pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <p style={{ fontSize: 11.5, color: 'var(--gold-500)', letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: 600 }}>
              {user?.corporate?.name || 'Your company'}
            </p>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 27, marginTop: 6, fontWeight: 600 }}>
              {event?.title || 'Select an event'}
            </h2>
            {event && (
              <p style={{ color: 'var(--text-mid)', fontSize: 13, marginTop: 5 }}>
                {fmtDate(event.startDate)} – {fmtDate(event.endDate)} · {event.venue?.name || event.venue?.city || 'Venue TBA'}
              </p>
            )}
          </div>
          <EventPicker />
        </div>
      </Card>

      {loading ? <Loader label="Loading event summary" /> : dash && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(215px, 1fr))', gap: 14, marginBottom: 20 }}>
            <Stat label="My Employees" value={fmtNumber(dash.participants.total)} icon={Users} tone="info"
              sub={`${dash.participants.vips} VIP`} />
            <Stat label="Checked In" value={fmtNumber(dash.participants.checkedIn)} icon={QrCode} tone="success"
              progress={pct(dash.participants.checkedIn, dash.participants.total)}
              sub={`${pct(dash.participants.checkedIn, dash.participants.total)}% arrived`} />
            <Stat label="Tickets Issued" value={fmtNumber(dash.travel.ticketsIssued)} icon={Plane} tone="gold"
              progress={pct(dash.travel.ticketsIssued, dash.participants.total)}
              sub={`${dash.travel.docsPending} awaiting documents`} />
            <Stat label="Rooms Occupied" value={`${dash.stay.roomsOccupied}/${dash.stay.roomsTotal}`} icon={BedDouble} tone="warning"
              progress={pct(dash.stay.roomsOccupied, dash.stay.roomsTotal)} />
            <Stat label="Boarded" value={fmtNumber(dash.transport.boarded)} icon={Bus} tone="info"
              sub={`${dash.transport.busesTotal} coaches`} />
          </div>

          {dash.travel.docsPending > 0 && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <Card padding={17} style={{
                marginBottom: 20, borderColor: 'rgba(251,191,36,0.28)', background: 'rgba(251,191,36,0.05)',
                display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
              }}>
                <FileWarning size={20} color="var(--warn)" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 200 }}>
                  <p style={{ fontSize: 13.5, fontWeight: 600 }}>
                    {dash.travel.docsPending} employee{dash.travel.docsPending === 1 ? '' : 's'} still owe travel documents
                  </p>
                  <p style={{ fontSize: 12.5, color: 'var(--text-mid)', marginTop: 3 }}>
                    Tickets cannot be issued until Aadhaar or passport details are verified.
                  </p>
                </div>
                <Link to="/corporate/flights">
                  <Button variant="outline" size="sm">Review travel readiness</Button>
                </Link>
              </Card>
            </motion.div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr)', gap: 16 }} className="gl-dash-grid">
            <Card padding={22}>
              <SectionTitle title="Travel compliance" subtitle="Where your team stands on documents and tickets" />
              {chartData.length ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={chartData} margin={{ top: 6, right: 6, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#24242B" vertical={false} />
                    <XAxis dataKey="name" tick={{ fill: '#71717F', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#71717F', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip
                      cursor={{ fill: 'rgba(239,212,138,0.06)' }}
                      contentStyle={{ background: '#16161A', border: '1px solid #24242B', borderRadius: 12, fontSize: 12 }}
                      labelStyle={{ color: '#EFD48A', fontWeight: 600 }}
                    />
                    <Bar dataKey="value" radius={[7, 7, 0, 0]} maxBarSize={58} {...chartMotion}>
                      {chartData.map((d) => <Cell key={d.name} fill={d.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <Loader />}
            </Card>

            <Card padding={22}>
              <SectionTitle title="Quick actions" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {[
                  { to: '/corporate/participants', label: 'Upload employee list', icon: Users },
                  { to: '/corporate/rooms', label: 'Allocate room partners', icon: BedDouble },
                  { to: '/corporate/flights', label: 'Track travel readiness', icon: Plane },
                  { to: '/corporate/attendance', label: 'View attendance', icon: QrCode },
                  { to: '/corporate/reports', label: 'Download reports', icon: Crown },
                ].map((a, i) => (
                  <motion.div key={a.to} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}>
                    <Link to={a.to}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px',
                        border: '1px solid var(--border)', borderRadius: 'var(--r)',
                        background: 'var(--ink-800)', fontSize: 13, transition: 'border-color 0.2s',
                      }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(239,212,138,0.32)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                      >
                        <a.icon size={15} color="var(--gold-500)" />
                        <span style={{ flex: 1 }}>{a.label}</span>
                        <ArrowRight size={13} color="var(--text-lo)" />
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </Card>
          </div>
        </>
      )}

      <style>{`
        @media (max-width: 1000px) { .gl-dash-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </motion.div>
  );
}
